import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Trash2, Lock, ShieldCheck, AlertTriangle, UserPlus } from "lucide-react";
import api, { getErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { NeonButton, NeonCard, Spinner, Pill } from "../../components/Neon";

const Label = ({ children }) => <label className="text-xs uppercase tracking-widest text-[var(--text-muted)]">{children}</label>;
const Input = (p) => <input className="mt-1 w-full bg-[var(--surface-2)] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[var(--neon-cyan)]" {...p} />;

export default function AdminStaff() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [available, setAvailable] = useState(true); // backend endpoint exists
  const [staff, setStaff] = useState([]);
  const [creating, setCreating] = useState(false);

  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [tempPw, setTempPw] = useState("");
  const [role, setRole] = useState("moderator");
  const [adminPw, setAdminPw] = useState("");

  const load = () =>
    api
      .get("/admin/staff")
      .then(({ data }) => {
        setAvailable(true);
        setStaff(Array.isArray(data) ? data : data.staff || []);
      })
      .catch((e) => {
        if (e?.response?.status === 404) setAvailable(false);
        else toast.error(getErr(e));
      })
      .finally(() => setLoading(false));
  useEffect(() => {
    load();
  }, []);

  const createStaff = async () => {
    if (!email || !fullName || !tempPw || !adminPw) {
      toast.error("Fill in all fields including your admin password.");
      return;
    }
    if (tempPw.length < 8) {
      toast.error("Temp password must be at least 8 characters.");
      return;
    }
    setCreating(true);
    try {
      await api.post("/admin/staff", { email, full_name: fullName, password: tempPw, role, admin_password: adminPw });
      toast.success(`${role === "admin" ? "Admin" : "Moderator"} account created.`);
      setEmail(""); setFullName(""); setTempPw(""); setAdminPw(""); setRole("moderator");
      load();
    } catch (e) {
      toast.error(getErr(e));
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (member) => {
    const pw = window.prompt(`Enter YOUR admin password to revoke ${member.email}:`);
    if (!pw) return;
    try {
      await api.post(`/admin/staff/${member.id}/revoke`, { admin_password: pw });
      toast.success("Access revoked.");
      load();
    } catch (e) {
      toast.error(getErr(e));
    }
  };

  if (loading) return <Spinner />;

  const roleColor = (r) => (String(r).toLowerCase() === "admin" ? "pink" : "cyan");

  return (
    <div data-testid="admin-staff" className="max-w-2xl">
      <div className="flex items-center gap-2">
        <Users className="h-6 w-6 text-[var(--neon-pink)]" />
        <h2 className="font-display uppercase text-2xl text-white">Staff &amp; Roles</h2>
      </div>

      {!available && (
        <div className="mt-4 flex items-start gap-3 rounded-xl border border-[rgba(255,184,0,.4)] bg-[rgba(255,184,0,.06)] p-4" data-testid="staff-patch-notice">
          <AlertTriangle className="h-5 w-5 text-[var(--neon-gold)] shrink-0" />
          <p className="text-sm text-[var(--text-muted)]">
            Staff &amp; Roles needs server support. Deploy <span className="text-white">BACKEND_PATCH_staff_roles.md</span> to your backend to list, add and revoke staff. The form below is ready and will work the moment the patch is live.
          </p>
        </div>
      )}

      {/* Current staff */}
      <NeonCard variant="flow" className="mt-4" innerClassName="p-5">
        <p className="font-display uppercase text-sm tracking-widest text-[var(--text-muted)]">Current staff</p>
        <div className="mt-3 divide-y divide-white/10" data-testid="staff-list">
          {staff.length === 0 && <p className="py-3 text-sm text-[var(--text-muted)]">No staff loaded yet.</p>}
          {staff.map((m) => {
            const isYou = user && (m.id === user.id || m.email === user.email);
            return (
              <div key={m.id || m.email} className="flex items-center justify-between gap-3 py-3">
                <span className="text-sm text-white truncate">
                  {m.email}
                  {isYou && <span className="text-[var(--text-muted)]"> (you)</span>}
                </span>
                <div className="flex items-center gap-3">
                  <Pill color={roleColor(m.role)}>{String(m.role || "").toUpperCase()}</Pill>
                  {!isYou && (
                    <button onClick={() => revoke(m)} className="text-[var(--neon-pink)] hover:brightness-110" data-testid={`revoke-${m.email}`} aria-label="Revoke">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </NeonCard>

      {/* Add staff */}
      <NeonCard variant="cyan" className="mt-4" innerClassName="p-5">
        <div className="flex items-center gap-2">
          <UserPlus className="h-5 w-5 text-[var(--neon-cyan)]" />
          <p className="font-display uppercase text-sm tracking-widest text-white">Add staff member</p>
        </div>
        <div className="mt-3 space-y-3">
          <div><Label>Email</Label><Input type="email" autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="staff-email" /></div>
          <div><Label>Full name</Label><Input value={fullName} onChange={(e) => setFullName(e.target.value)} data-testid="staff-name" /></div>
          <div><Label>Temp password (min 8)</Label><Input type="password" autoComplete="new-password" value={tempPw} onChange={(e) => setTempPw(e.target.value)} data-testid="staff-temp-pw" /></div>
          <div>
            <Label>Role</Label>
            <div className="mt-1 flex gap-2">
              <button onClick={() => setRole("moderator")} data-testid="role-moderator" className={`rounded-full px-5 py-2 text-sm font-display uppercase tracking-wide ${role === "moderator" ? "bg-[var(--neon-pink)] text-black" : "border border-white/15 text-[var(--text-muted)]"}`}>Moderator</button>
              <button onClick={() => setRole("admin")} data-testid="role-admin" className={`rounded-full px-5 py-2 text-sm font-display uppercase tracking-wide ${role === "admin" ? "bg-[var(--neon-pink)] text-black" : "border border-white/15 text-[var(--text-muted)]"}`}>Full Admin</button>
            </div>
          </div>
          <div className="flex items-start gap-2 rounded-xl border border-[rgba(255,184,0,.5)] p-3">
            <Lock className="h-4 w-4 text-[var(--neon-gold)] shrink-0 mt-0.5" />
            <p className="text-xs text-[var(--text-muted)]">Authorise this change with YOUR password (also required to revoke).</p>
          </div>
          <div><Label>Your admin password</Label><Input type="password" autoComplete="current-password" value={adminPw} onChange={(e) => setAdminPw(e.target.value)} data-testid="staff-admin-pw" /></div>
          <NeonButton variant="pink" className="w-full" onClick={createStaff} loading={creating} data-testid="staff-create">
            <ShieldCheck className="h-4 w-4" /> Create Account
          </NeonButton>
        </div>
      </NeonCard>
    </div>
  );
}
